// V1 source: customer/midas/components/MidasArtifactMachineCard.tsx
// Strategy: wraps-platform-component
// Verified no-MUI: yes (this wrapper file is MUI-free; the wrapped
//   <ProfileSidebarCard> uses MUI internally, which is the Round 10
//   invariant — wrapped platform components keep their V1 styling.)
//
// V2 machine card viewer — Tier B forward-compat. Delegates rendering
// to the platform's <ProfileSidebarCard> (the same card used on the
// machine detail page sidebar) so the dock surface matches the rest of
// the platform.
//
// Why this differs from V1's MidasArtifactMachineCard:
//   V1 had its own custom card chrome built from the smaller
//   `MachineCardArtifactData` payload (image + manufacturer + model +
//   spec rows + open SR count). V2 wraps the canonical platform card
//   instead. The artifact payload only carries `machine_id`; we
//   dispatch `getMachine(id)` and render <ProfileSidebarCard machine=…>
//   once the entity is loaded.
//
// Required context:
//   - Redux store (provided by the app at /midas-v2 — same as the chat
//     panel and the SR Detail viewer).
//   - `getMachine` thunk + `customerMachinerySlice.machine` selector.
//   - i18n `myMachinery` namespace (consumed by ProfileSidebarCard).
//   No new context wiring needed inside the dock.
//
// Render states (Round-15 hardening — the wrapper MUST never render
// blank, even on a malformed/unknown `machine_id` from the bot):
//   1. No payload (artifact `data` missing)        → noData fallback.
//   2. Payload missing `machine_id`                → notFound fallback.
//   3. Slice not yet populated for this id         → visible skeleton.
//   4. Local fetch errored (e.g. 404 on bad UUID)  → notFound fallback.
//   5. Slice populated for this id                 → ProfileSidebarCard.
// We track fetch state locally (rather than reading the shared
// `customerMachinery.error` slice) because that error is global and
// could be set by a sibling page; the wrapper must only react to the
// fetch IT just dispatched.

import React, { useEffect, useState } from "react";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import { getMachine } from "../../../machinery/customerMachinerySlice";
import ProfileSidebarCard from "../../../machinery/components/ProfileSidebarCard";
import { ArtifactTab } from "../../midasPageModels";
import { MachineCardArtifactData } from "../../../../shared/chatBot/models/chatBotModels";
import useTranslation from "../../../../shared/hooks/useTranslation";

interface Props {
  tab: ArtifactTab;
}

type FetchState = "idle" | "loading" | "loaded" | "error";

const MidasArtifactMachineCardV2: React.FC<Props> = ({ tab }) => {
  const { translate } = useTranslation("chatBot");
  const { translate: translateMachinery } = useTranslation("myMachinery");
  const dispatch = useAppDispatch();
  const data = tab.artifact.data as MachineCardArtifactData | undefined;

  // Pull the (possibly cached) full Machine entity from Redux.
  const machineState = useAppSelector(
    (state: any) => state.customerMachinery.machine
  );

  // Local fetch state — kept in this wrapper instance so we don't have to
  // share a global `error` slot with sibling pages.
  const [fetchState, setFetchState] = useState<FetchState>("idle");

  // If the artifact id doesn't match what's currently in the slice (or the
  // slice is empty), kick off a fetch. The platform card only needs a
  // `Machine` prop — once the slice is populated we pass it down.
  const machineId = data?.machine_id;

  useEffect(() => {
    if (!machineId) return;
    if (machineState?.id === machineId) {
      // Already in slice — no fetch needed, mark loaded.
      setFetchState("loaded");
      return;
    }
    let cancelled = false;
    setFetchState("loading");
    // `unwrap()` rethrows on rejection so we can branch our local state.
    dispatch(getMachine(machineId))
      .unwrap()
      .then(() => {
        if (!cancelled) setFetchState("loaded");
      })
      .catch(() => {
        if (!cancelled) setFetchState("error");
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, machineId, machineState?.id]);

  // ---- 1. No artifact payload at all ------------------------------------
  if (!data) {
    return (
      <div className="flex flex-1 items-center justify-center font-sora text-[13px] text-midas-muted-stone">
        {translate("midas.artifact.noData")}
      </div>
    );
  }

  // ---- 2. Payload present but missing/blank machine_id ------------------
  // Bot could emit `{ machine_id: "" }` or omit the field — render the
  // same not-found state we use after a 404.
  if (!machineId) {
    return (
      <div className="flex flex-1 items-center justify-center px-6 py-10 font-sora text-[13px] text-midas-graphite">
        {translateMachinery("machineNotFound")}
      </div>
    );
  }

  // ---- 4. Fetch errored (e.g. 404 on a malformed UUID) ------------------
  // Surface this BEFORE the slice check so a stale `state.machine` from a
  // sibling page can't leak through and look like success.
  if (fetchState === "error") {
    return (
      <div className="flex flex-1 items-center justify-center px-6 py-10 font-sora text-[13px] text-midas-graphite">
        {translateMachinery("machineNotFound")}
      </div>
    );
  }

  // ---- 3. Loading: slice not yet populated for this id ------------------
  // Visible skeleton (Phase 2C convention: solid `bg-midas-warm-stone`
  // with `animate-pulse`). Mimics the rough shape of ProfileSidebarCard:
  // header + image area + spec rows. Bordered + flex-fills the dock body
  // so it never collapses to zero height. `m-3` matches the `p-3` gutter
  // around the loaded card below.
  if (fetchState !== "loaded" || !machineState || machineState.id !== machineId) {
    return (
      <div
        className="m-3 flex flex-col gap-3 rounded-md border border-midas-stone bg-white p-5"
        aria-hidden="true"
      >
        {/* Image placeholder */}
        <div className="h-[140px] w-full animate-pulse rounded-md bg-midas-warm-stone" />
        {/* Title placeholder */}
        <div className="mx-auto h-4 w-3/5 animate-pulse rounded bg-midas-warm-stone" />
        {/* Subtitle placeholder */}
        <div className="mx-auto h-3 w-2/5 animate-pulse rounded bg-midas-warm-stone" />
        {/* Spec rows */}
        <div className="mt-2 flex flex-col gap-2 border-t border-dashed border-midas-stone pt-3">
          <div className="h-3 w-full animate-pulse rounded bg-midas-warm-stone" />
          <div className="h-3 w-full animate-pulse rounded bg-midas-warm-stone" />
          <div className="h-3 w-4/5 animate-pulse rounded bg-midas-warm-stone" />
          <div className="h-3 w-3/5 animate-pulse rounded bg-midas-warm-stone" />
        </div>
      </div>
    );
  }

  // ---- 5. Wrap the canonical platform card ------------------------------
  // Body padding mirrors V1 dock chrome (12px gutter inside the bordered
  // content area). The card itself owns its border + shadow + padding —
  // we just provide an overflow-auto viewport so taller cards scroll
  // inside the dock.
  return (
    <div className="flex flex-1 flex-col overflow-auto p-3">
      <ProfileSidebarCard machine={machineState} />
    </div>
  );
};

export default MidasArtifactMachineCardV2;
